"use client";

import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { X, Upload, File, Video } from "lucide-react";
import { fileValidation } from "@/services/kycService";

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  accept: string;
  maxSize: number;
  fileType: "image" | "video";
  label: string;
  description?: string;
  error?: string;
  required?: boolean;
  // currentFile can be a File (local) or a string URL from the API
  currentFile?: File | string | null;
  hasKYCError?: boolean;
  isUploading?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  accept,
  maxSize,
  fileType,
  label,
  description,
  error,
  required = false,
  currentFile,
  hasKYCError = false,
  isUploading = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File) => {
      setValidationError(null);

      if (fileType === "image") {
        const validation = fileValidation.validateImageFile(file, maxSize);
        if (!validation.isValid) {
          setValidationError(validation.error || "Invalid file");
          return false;
        }
      } else if (fileType === "video") {
        const validation = fileValidation.validateVideoFile(file, maxSize);
        if (!validation.isValid) {
          setValidationError(validation.error || "Invalid file");
          return false;
        }
      }

      return true;
    },
    [fileType, maxSize],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setDragActive(false);

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (validateFile(file)) {
          onFileSelect(file);
        }
      }
    },
    [onFileSelect, validateFile],
  );

  const onDragEnter = useCallback(() => {
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter,
    onDragLeave,
    accept: {
      [accept]: [],
    },
    maxFiles: 1,
    multiple: false,
  });

  const removeFile = () => {
    onFileSelect(null);
    setValidationError(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = () => {
    if (fileType === "video") {
      return <Video className="w-8 h-8 text-auth-text-muted" />;
    }
    return <File className="w-8 h-8 text-auth-text-muted" />;
  };

  const displayError = error || validationError;

  return (
    <div className="w-full">
      {/* Label */}
      <div className="mb-1">
        <label className="text-auth-text-muted text-sm font-normal">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      </div>

      {/* File Upload Area */}
      <div
        {...getRootProps()}
        className={`
          relative border rounded-[30px] bg-auth-card p-6 cursor-pointer transition-all duration-200
          ${hasKYCError ? "border-orange-500 bg-orange-50" : "border-auth-border"}
          ${isDragActive || dragActive ? "border-brand bg-auth-surface" : ""}
          ${displayError && !hasKYCError ? "border-red-500" : ""}
          ${currentFile && !hasKYCError ? "border-brand" : ""}
        `}
      >
        <input {...getInputProps()} disabled={isUploading} />

        {isUploading ? (
          // Upload Progress State
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
            </div>
            <p className="text-foreground text-sm font-medium mb-1">
              Uploading {fileType}...
            </p>
            <p className="text-muted-foreground text-xs">
              Please wait while we process your file
            </p>
          </div>
        ) : currentFile ? (
          // File Selected State (local File or remote URL)
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getFileIcon()}
              <div>
                {typeof currentFile === "string" ? (
                  // Remote URL preview — show thumbnail for images
                  fileType === "image" ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={currentFile}
                        alt={label}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div>
                        <p className="text-white text-sm font-medium truncate max-w-[260px]">
                          {currentFile}
                        </p>
                        <p className="text-auth-text-muted text-xs">
                          Remote file
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-white text-sm font-medium truncate">
                        {currentFile}
                      </p>
                      <p className="text-auth-text-muted text-xs">
                        Remote file
                      </p>
                    </>
                  )
                ) : (
                  <>
                    <p className="text-white text-sm font-medium">
                      {currentFile.name}
                    </p>
                    <p className="text-auth-text-muted text-xs">
                      {formatFileSize(currentFile.size)}
                    </p>
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              className="p-1 hover:bg-auth-surface-dark rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-auth-text-muted" />
            </button>
          </div>
        ) : (
          // Upload State
          <div className="text-center">
            <div
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-3 transition-all duration-200 ${
                isDragActive || dragActive
                  ? "bg-primary/20 scale-110"
                  : "bg-muted/50"
              }`}
            >
              <Upload
                className={`w-6 h-6 transition-colors duration-200 ${
                  isDragActive || dragActive
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
            </div>
            <p
              className={`text-sm font-medium mb-1 transition-colors duration-200 ${
                isDragActive || dragActive ? "text-primary" : "text-foreground"
              }`}
            >
              {isDragActive || dragActive
                ? "Drop file here"
                : "Click to upload or drag and drop"}
            </p>
            {description && (
              <p className="text-muted-foreground text-xs">{description}</p>
            )}
            <p className="text-muted-foreground text-xs mt-1">
              Max size: {formatFileSize(maxSize)}
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {displayError && (
        <p className="text-red-500 text-xs mt-1">{displayError}</p>
      )}
    </div>
  );
};

export default FileUpload;
